/** QuickPlan — Repères hiérarchiques : attaches parent / enfant et articulations.
 *
 * Repère d'une instance :  F(inst) = F(parent) · L(inst) · J(inst)
 *   L = placement propre (pos, rot) exprimé dans le repère du parent (monde si racine)
 *   J = articulation du composant (charnière ou glissière) à l'ouverture `inst.open`
 * La géométrie de l'instance ET ses enfants vivent dans F(inst) : une poignée attachée
 * à une porte suit la porte quand on l'ouvre. Même calcul que la vue 3D (groupes
 * imbriqués three.js), si bien que serveur, IA et éditeur voient la même chose.
 */
import { rotMatrix, apply, add, sub } from './resolve.js';

export const IDENT = Object.freeze({ R: [[1, 0, 0], [0, 1, 0], [0, 0, 1]], t: [0, 0, 0] });

const mulR = (a, b) => a.map((row) => [0, 1, 2].map((j) => row[0] * b[0][j] + row[1] * b[1][j] + row[2] * b[2][j]));
const transpose = (m) => [0, 1, 2].map((i) => [m[0][i], m[1][i], m[2][i]]);

/** a · b (b exprimé dans a). */
export function compose(a, b) {
  return { R: mulR(a.R, b.R), t: add(apply(a.R, b.t), a.t) };
}
export function invert(f) {
  const Rt = transpose(f.R);
  return { R: Rt, t: apply(Rt, f.t).map((v) => -v) };
}
export const point = (f, p) => add(apply(f.R, p), f.t);
export const vector = (f, v) => apply(f.R, v);

/** Rotation d'angle `deg` autour de l'axe `axis` (Rodrigues). */
export function axisAngle(axis, deg) {
  const n = Math.hypot(...axis) || 1;
  const [x, y, z] = axis.map((v) => v / n);
  const a = (deg * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  const k = 1 - c;
  return [
    [c + x * x * k, x * y * k - z * s, x * z * k + y * s],
    [y * x * k + z * s, c + y * y * k, y * z * k - x * s],
    [z * x * k - y * s, z * y * k + x * s, c + z * z * k],
  ];
}

export const localFrame = (inst) => ({ R: rotMatrix(inst.rot ?? [0, 0, 0]), t: inst.pos ?? [0, 0, 0] });

/** Repère d'articulation pour une ouverture donnée (bornée). */
export function jointFrame(joint, open) {
  if (!joint || !open) return IDENT;
  const v = Math.max(joint.min, Math.min(joint.max, open));
  if (joint.type === 'slide') {
    const n = Math.hypot(...joint.axis) || 1;
    return { R: IDENT.R, t: joint.axis.map((a) => (a / n) * v) };
  }
  const R = axisAngle(joint.axis, v);
  const o = joint.origin;
  return { R, t: sub(o, apply(R, o)) };
}

/**
 * Repères de toutes les instances : Map id → F. `resolveOf(inst)` rend le composant
 * résolu (ou null). Une boucle de parenté (données corrompues) est coupée : l'instance
 * est alors traitée comme racine.
 */
export function computeFrames(project, resolveOf) {
  const byId = new Map(project.instances.map((i) => [i.id, i]));
  const out = new Map();
  const visiting = new Set();
  const frameOf = (inst) => {
    const done = out.get(inst.id);
    if (done) return done;
    let parentF = IDENT;
    if (inst.parentId && byId.has(inst.parentId) && !visiting.has(inst.id)) {
      visiting.add(inst.id);
      parentF = frameOf(byId.get(inst.parentId));
      visiting.delete(inst.id);
    }
    const res = resolveOf(inst);
    const f = compose(compose(parentF, localFrame(inst)), jointFrame(res?.joint, inst.open));
    out.set(inst.id, f);
    return f;
  };
  for (const i of project.instances) frameOf(i);
  return out;
}

/** Repère dans lequel vivent les enfants d'un parent (= son F), IDENT pour la racine. */
export function parentFrameOf(frames, inst) {
  return inst.parentId ? frames.get(inst.parentId) ?? IDENT : IDENT;
}

/** Ids des descendants (enfants, petits-enfants…) d'une instance. */
export function descendants(project, id) {
  const out = new Set();
  let grew = true;
  while (grew) {
    grew = false;
    for (const i of project.instances) {
      if (i.parentId && (i.parentId === id || out.has(i.parentId)) && !out.has(i.id) && i.id !== id) {
        out.add(i.id);
        grew = true;
      }
    }
  }
  return out;
}

/** Boîte englobante monde d'une instance résolue dans son repère F. */
export function bboxInFrame(f, res) {
  const { min: lo, max: hi } = res.bbox;
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  for (const x of [lo[0], hi[0]]) for (const y of [lo[1], hi[1]]) for (const z of [lo[2], hi[2]]) {
    const p = point(f, [x, y, z]);
    for (let k = 0; k < 3; k++) { if (p[k] < min[k]) min[k] = p[k]; if (p[k] > max[k]) max[k] = p[k]; }
  }
  const r = (v) => Math.round(v * 1000) / 1000;
  return { min: min.map(r), max: max.map(r) };
}

export function portsInFrame(f, res) {
  const r = (v) => Math.round(v * 1000) / 1000;
  return res.ports.map((p) => ({ id: p.id, label: p.label, pos: point(f, p.pos).map(r), dir: p.dir ? vector(f, p.dir).map(r) : null }));
}
